import { useEffect, useState } from 'react'
import { offer } from '../data/offer'

export type TimeLeft = {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const STORAGE_KEY = 'lumen-offer-deadline'

/**
 * Counts down to an absolute timestamp so refreshing the page never resets it.
 * Uses the real closing date when configured; otherwise a per-visitor deadline
 * persisted in localStorage. Returns null when nothing is configured or expired.
 */
export function useDeadlineCountdown(
  closesAt: string | null = offer.closesAt,
  evergreenHours: number | null = offer.evergreenHours,
): TimeLeft | null {
  const [target] = useState(() => resolveTarget(closesAt, evergreenHours))
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(() =>
    computeTimeLeft(target),
  )

  useEffect(() => {
    if (Number.isNaN(target)) return
    const id = setInterval(() => setTimeLeft(computeTimeLeft(target)), 1000)
    return () => clearInterval(id)
  }, [target])

  return timeLeft
}

function resolveTarget(
  closesAt: string | null,
  evergreenHours: number | null,
): number {
  if (closesAt) return new Date(closesAt).getTime()
  if (!evergreenHours) return NaN
  const fresh = Date.now() + evergreenHours * 3600 * 1000
  try {
    const stored = Number(window.localStorage.getItem(STORAGE_KEY))
    if (stored && stored > Date.now()) return stored
    window.localStorage.setItem(STORAGE_KEY, String(fresh))
  } catch {
    // localStorage unavailable (private mode) — fall back to session deadline
  }
  return fresh
}

function computeTimeLeft(target: number): TimeLeft | null {
  if (Number.isNaN(target)) return null
  const diff = target - Date.now()
  if (diff <= 0) return null
  const totalSeconds = Math.floor(diff / 1000)
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  }
}

export function pad(n: number): string {
  return n.toString().padStart(2, '0')
}
